import React, { useState, useMemo } from 'react';
import { useQuery } from 'react-query';
import inventoryService, { PendingApprovalLot, LotsSummary, ByproductSummaryItem } from '../../services/inventoryService';
import productionService from '../../services/productionService';
import { useLanguage } from '../../i18n/LanguageContext';
import { useAuth } from '../../hooks/useAuth';
import { useDashboardWebSocket } from '../../hooks/useDashboardWebSocket';
import IncompleteSessionsModal from '../../components/IncompleteSessionsModal';
import PendingApprovalsModal from './components/PendingApprovalsModal';
import StationOverview from '../../components/StationOverview';
import KpiDetailModal, { KpiDetailType } from '../../components/KpiDetailModal';
import workScheduleService, { ManpowerSummary, ScheduledWorkforceResponse } from '../../services/workScheduleService';
import { ClipboardList, Users, ClipboardCheck, Package } from 'lucide-react';
import YieldVarianceSuggestionsBanner from '../../components/YieldVarianceSuggestionsBanner';
import { QUERY_REFETCH_NORMAL, QUERY_REFETCH_SLOW, isElevatedRole } from '../../constants/queryConfig';

const SupervisorDashboard: React.FC = () => {
  const { t } = useLanguage();
  const { user } = useAuth();

  // Real-time WebSocket updates for dashboard data
  useDashboardWebSocket();
  const [isIncompleteSessionsModalOpen, setIsIncompleteSessionsModalOpen] = useState(false);
  const [isPendingApprovalsModalOpen, setIsPendingApprovalsModalOpen] = useState(false);
  const [activeKpiDetail, setActiveKpiDetail] = useState<KpiDetailType>(null);

  // Fetch all production sessions to check for incomplete ones from previous days
  const { data: allSessions = [] } = useQuery(
    ['all-production-sessions'],
    () => productionService.getProductionSessions({ include: 'sub_sessions' }),
    {
      staleTime: QUERY_REFETCH_SLOW, // Fresh for 1 minute
    }
  );

  // Calculate incomplete sessions from previous days
  const incompleteSessions = React.useMemo(() => {
    const today = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD format
    return allSessions.filter((session: any) => {
      const scheduledDate = session.scheduled_date?.split('T')[0];
      const isBeforeToday = scheduledDate && scheduledDate < today;
      const isIncomplete = !['completed', 'cancelled'].includes(session.status);
      return isBeforeToday && isIncomplete;
    });
  }, [allSessions]);

  // Check if user is manager or admin (can approve on-hold items)
  const isManagerOrAdmin = isElevatedRole(user?.role);

  // Fetch pending approvals for managers/admins
  const { data: pendingApprovals = [], isLoading: pendingApprovalsLoading, refetch: refetchPendingApprovals } = useQuery<PendingApprovalLot[]>(
    ['pending-approvals'],
    () => inventoryService.getPendingApprovals(),
    {
      enabled: isManagerOrAdmin,
      refetchInterval: QUERY_REFETCH_SLOW, // Refresh every minute
      staleTime: QUERY_REFETCH_NORMAL,
    }
  );

  // Fetch today's manpower summary (operators, packers, stations scheduled)
  const todayDate = new Date().toLocaleDateString('en-CA');
  const { data: manpower } = useQuery<ManpowerSummary>(
    ['manpower-summary', todayDate],
    () => workScheduleService.getManpowerSummary(todayDate),
    {
      staleTime: QUERY_REFETCH_NORMAL,
      refetchOnMount: 'always',
    }
  );

  // Fetch lot inventory summary (remaining material lots)
  const { data: lotsSummary } = useQuery<LotsSummary>(
    ['lots-summary'],
    () => inventoryService.getLotsSummary(),
    {
      staleTime: 60000,
    }
  );

  // Fetch workforce detail (individual employees) — only when workforce modal is open
  const { data: workforce } = useQuery<ScheduledWorkforceResponse>(
    ['scheduled-workforce', todayDate],
    () => workScheduleService.getScheduledWorkforce(todayDate),
    {
      enabled: activeKpiDetail === 'workforce',
      staleTime: 30000,
    }
  );

  // Fetch individual lots on demand (only when lots modal is open)
  const { data: lotsData, isLoading: lotsLoading } = useQuery(
    ['kpi-lots-detail'],
    () => inventoryService.getLots({ status: 'remaining', limit: 250 }),
    {
      enabled: activeKpiDetail === 'lots',
      staleTime: 60000,
    }
  );

  // Fetch byproduct inventory summary (only when lots modal is open)
  const { data: byproducts = [], isLoading: byproductsLoading } = useQuery<ByproductSummaryItem[]>(
    ['kpi-byproduct-summary'],
    () => inventoryService.getByproductSummary(),
    {
      enabled: activeKpiDetail === 'lots',
      staleTime: 60000,
    }
  );

  // Today's sessions (non-cancelled, for KPI cards and Production Progress)
  const todaySessions = useMemo(() => {
    const todayStr = new Date().toLocaleDateString('en-CA');
    return allSessions.filter((s: any) =>
      s.scheduled_date?.split('T')[0] === todayStr && s.status !== 'cancelled'
    );
  }, [allSessions]);

  // Session stats from todaySessions
  const sessionStats = useMemo(() => {
    const planned = todaySessions.filter((s: any) => s.status === 'pending' || s.status === 'submitted' || s.status === 'received' || s.status === 'ready').length;
    const active = todaySessions.filter((s: any) => s.status === 'active' || s.status === 'in_progress').length;
    const completed = todaySessions.filter((s: any) => s.status === 'completed').length;
    return { total: todaySessions.length, planned, active, completed };
  }, [todaySessions]);

  return (
    <div data-tour="supervisor-dashboard" className="flex flex-col gap-3 min-h-full">
      {/* KPI Cards — manager-style layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        {/* Card 1: Today's Sessions */}
        <div
          onClick={() => setActiveKpiDetail('sessions')}
          className="bg-white dark:bg-gray-800 rounded-lg shadow p-3 2xl:p-5 cursor-pointer hover:ring-2 hover:ring-indigo-300 dark:hover:ring-indigo-600 transition-shadow"
        >
          <div className="flex items-center justify-between mb-2 2xl:mb-4">
            <div className="flex items-center gap-2">
              <div className="p-1.5 2xl:p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-full">
                <ClipboardList className="w-4 h-4 2xl:w-5 2xl:h-5 text-indigo-600 dark:text-indigo-400" />
              </div>
              <p className="text-sm 2xl:text-lg font-semibold text-gray-500 dark:text-gray-400">
                {t('supervisor.todaysSessions')}
              </p>
            </div>
            <p className="text-2xl 2xl:text-3xl font-bold text-gray-900 dark:text-white">
              {sessionStats.total}
            </p>
          </div>
          <div className="grid grid-cols-3 gap-2 2xl:gap-3 text-sm">
            <div className="text-center border border-blue-200 dark:border-blue-800 rounded-lg py-1.5 2xl:py-3 px-2">
              <p className="text-xs 2xl:text-sm text-gray-500 dark:text-gray-400">{t('supervisor.planned')}</p>
              <p className="text-lg 2xl:text-2xl font-bold text-blue-600 dark:text-blue-400">{sessionStats.planned}</p>
            </div>
            <div className="text-center border border-green-200 dark:border-green-800 rounded-lg py-1.5 2xl:py-3 px-2">
              <p className="text-xs 2xl:text-sm text-gray-500 dark:text-gray-400">{t('supervisor.activeLabel')}</p>
              <p className="text-lg 2xl:text-2xl font-bold text-green-600 dark:text-green-400">{sessionStats.active}</p>
            </div>
            <div className="text-center border border-gray-200 dark:border-gray-700 rounded-lg py-1.5 2xl:py-3 px-2">
              <p className="text-xs 2xl:text-sm text-gray-500 dark:text-gray-400">{t('supervisor.completedLabel')}</p>
              <p className="text-lg 2xl:text-2xl font-bold text-gray-600 dark:text-gray-300">{sessionStats.completed}</p>
            </div>
          </div>
        </div>

        {/* Card 2: Today's Workforce */}
        <div
          onClick={() => setActiveKpiDetail('workforce')}
          className="bg-white dark:bg-gray-800 rounded-lg shadow p-3 2xl:p-5 cursor-pointer hover:ring-2 hover:ring-cyan-300 dark:hover:ring-cyan-600 transition-shadow"
        >
          <div className="flex items-center justify-between mb-2 2xl:mb-4">
            <div className="flex items-center gap-2">
              <div className="p-1.5 2xl:p-2 bg-cyan-100 dark:bg-cyan-900/30 rounded-full">
                <Users className="w-4 h-4 2xl:w-5 2xl:h-5 text-cyan-600 dark:text-cyan-400" />
              </div>
              <p className="text-sm 2xl:text-lg font-semibold text-gray-500 dark:text-gray-400">
                {t('manager.todaysWorkforce')}
              </p>
            </div>
            <p className="text-2xl 2xl:text-3xl font-bold text-gray-900 dark:text-white">
              {manpower ? (manpower.summary.operators + manpower.summary.packers) : '-'}
            </p>
          </div>
          <div className="grid grid-cols-3 gap-2 2xl:gap-3 text-sm">
            <div className="text-center border border-green-200 dark:border-green-800 rounded-lg py-1.5 2xl:py-3 px-2">
              <p className="text-xs 2xl:text-sm text-gray-500 dark:text-gray-400">{t('manager.operators')}</p>
              <p className="text-lg 2xl:text-2xl font-bold text-green-600 dark:text-green-400">{manpower?.summary?.operators ?? '-'}</p>
            </div>
            <div className="text-center border border-blue-200 dark:border-blue-800 rounded-lg py-1.5 2xl:py-3 px-2">
              <p className="text-xs 2xl:text-sm text-gray-500 dark:text-gray-400">{t('manager.packers')}</p>
              <p className="text-lg 2xl:text-2xl font-bold text-blue-600 dark:text-blue-400">{manpower?.summary?.packers ?? '-'}</p>
            </div>
            <div className="text-center border border-purple-200 dark:border-purple-800 rounded-lg py-1.5 2xl:py-3 px-2">
              <p className="text-xs 2xl:text-sm text-gray-500 dark:text-gray-400">{t('manager.stations')}</p>
              <p className="text-lg 2xl:text-2xl font-bold text-purple-600 dark:text-purple-400">{manpower?.summary?.stations ?? '-'}</p>
            </div>
          </div>
        </div>

        {/* Card 3: Station Overview — hidden per TBHTE-434 */}
        {/* Card 4: Today's Production — hidden per TBHTE-434 */}

        {/* Card 3: Material Lots */}
        <div
          onClick={() => setActiveKpiDetail('lots')}
          className="bg-white dark:bg-gray-800 rounded-lg shadow p-3 2xl:p-5 cursor-pointer hover:ring-2 hover:ring-orange-300 dark:hover:ring-orange-600 transition-shadow"
        >
          <div className="flex items-center justify-between mb-2 2xl:mb-4">
            <div className="flex items-center gap-2">
              <div className="p-1.5 2xl:p-2 bg-orange-100 dark:bg-orange-900/30 rounded-full">
                <Package className="w-4 h-4 2xl:w-5 2xl:h-5 text-orange-600 dark:text-orange-400" />
              </div>
              <p className="text-sm 2xl:text-lg font-semibold text-gray-500 dark:text-gray-400">
                {t('manager.remainingMaterialLots')}
              </p>
            </div>
            <p className="text-2xl 2xl:text-3xl font-bold text-gray-900 dark:text-white">
              {lotsSummary ? lotsSummary.available_lots : '-'}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-2 2xl:gap-3 text-sm">
            <div className="text-center border border-orange-200 dark:border-orange-800 rounded-lg py-1.5 2xl:py-3 px-2">
              <p className="text-xs 2xl:text-sm text-gray-500 dark:text-gray-400">{t('manager.lots')}</p>
              <p className="text-lg 2xl:text-2xl font-bold text-orange-600 dark:text-orange-400">{lotsSummary ? lotsSummary.available_lots : '-'}</p>
            </div>
            <div className="text-center border border-orange-200 dark:border-orange-800 rounded-lg py-1.5 2xl:py-3 px-2">
              <p className="text-xs 2xl:text-sm text-gray-500 dark:text-gray-400">{t('manager.weight')}</p>
              <p className="text-lg 2xl:text-2xl font-bold text-orange-600 dark:text-orange-400">{lotsSummary ? Number(lotsSummary.total_available_weight).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}<span className="text-xs 2xl:text-sm font-normal text-gray-400 ml-1">lbs</span></p>
            </div>
          </div>
        </div>

        {/* Alert Cards — conditional */}
        {isManagerOrAdmin && pendingApprovals.length > 0 && (
          <div
            onClick={() => setIsPendingApprovalsModalOpen(true)}
            className="bg-white dark:bg-gray-800 rounded-lg shadow p-3 xl:p-5 hover:shadow-lg hover:ring-2 hover:ring-amber-300 dark:hover:ring-amber-700 transition-all cursor-pointer"
          >
            <div className="flex items-center justify-between mb-2 xl:mb-4">
              <div className="flex items-center gap-2">
                <div className="p-1.5 xl:p-2 bg-amber-100 dark:bg-amber-900/30 rounded-full">
                  <ClipboardCheck className="w-4 h-4 xl:w-5 xl:h-5 text-amber-600 dark:text-amber-400" />
                </div>
                <p className="text-base xl:text-lg font-semibold text-gray-500 dark:text-gray-400">
                  {t('supervisor.pendingApprovalsAlert')}
                </p>
              </div>
              <p className="text-2xl xl:text-3xl font-bold text-amber-600 dark:text-amber-400">
                {pendingApprovals.length}
              </p>
            </div>
            <p className="text-xs text-amber-600 dark:text-amber-400">
              {t('common.clickToViewDetails')}
            </p>
          </div>
        )}
      </div>

      {/* Recurring Yield Variance Suggestions */}
      <YieldVarianceSuggestionsBanner compact />

      {/* Station Progress Overview — uses shared component */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 xl:p-5 flex-1 flex flex-col">
        <StationOverview />
      </div>

      {/* Incomplete Sessions Modal */}
      <IncompleteSessionsModal
        isOpen={isIncompleteSessionsModalOpen}
        onClose={() => setIsIncompleteSessionsModalOpen(false)}
        sessions={incompleteSessions}
      />

      {/* Pending Approvals Modal */}
      {isManagerOrAdmin && (
        <PendingApprovalsModal
          isOpen={isPendingApprovalsModalOpen}
          onClose={() => setIsPendingApprovalsModalOpen(false)}
          pendingItems={pendingApprovals}
          onRefresh={() => refetchPendingApprovals()}
          loading={pendingApprovalsLoading}
        />
      )}

      {/* KPI Detail Modal — opens when any KPI card is clicked */}
      <KpiDetailModal
        type={activeKpiDetail}
        onClose={() => setActiveKpiDetail(null)}
        todaySessions={todaySessions}
        workforce={workforce}
        lots={lotsData?.lots || []}
        lotsLoading={lotsLoading}
        byproducts={byproducts}
        byproductsLoading={byproductsLoading}
      />
    </div>
  );
};

export default SupervisorDashboard;
